/**
 * A Pub 1220 foreign address, read back into city, province, postal code and country.
 *
 * When a record's foreign indicator is "1", Pub 1220 lets the filer write the city, state and
 * ZIP positions as one free-format block: "city, province or state, postal code, and the name
 * of the country", in that order (Pub 1220 Rev. 5-2026, Part C: B record position 287, A record
 * positions 134-173, T record 190-229, 1099-LTC insured 637-687). For the B record that block
 * is positions 448-498: 51 characters across what is otherwise City (40), State (2), ZIP (9).
 * Reading those 51 characters as City, State and ZIP, as a US address is read, cuts the
 * country off and puts half the postal code in the state.
 *
 * Writers do not all follow the stated order, so this reads what it can prove and says how sure
 * it is:
 *  - high: the country is named, the postal code has that country's shape (or the country has
 *    no postal codes), and nothing is left over.
 *  - medium: a reasonable reading that a person should glance at: the country came from a
 *    two-letter code without supporting evidence, was found outside the block, or was inferred
 *    from a postal code; or the postal code could not be checked against a known format.
 *  - low: no safe reading. The block names no country, a code could mean two countries, the
 *    address looks like a US one, or the postal code contradicts the country.
 *
 * Nothing here guesses past that. A low reading keeps the original text in the city so nothing
 * is lost, and every reading carries its reasons, so it can be reviewed or handed to a later
 * pass. The module is pure: text in, a reading out.
 *
 * Postal code shapes marked `verified` come from the UPU country addressing sheets; the others
 * are the widely published national formats and only ever yield a medium reading on their own.
 */
import { IRS_COUNTRY_CODES } from "../validate/countryCodes.generated";

export type ForeignAddressConfidence = "high" | "medium" | "low";

export interface ForeignAddressReading {
  /** The block exactly as the file had it, trimmed. */
  raw: string;
  city: string;
  /** Province or state as written (a code where the country uses codes). */
  province: string;
  /** The province's full name when it is a known code, else the same as `province`. */
  provinceName: string;
  postalCode: string;
  /** The IRS CountryType code (UK, GM, AS, ...), or null when no country could be settled. */
  countryCode: string | null;
  /** The IRS name for that code, for printing. Empty when there is no code. */
  countryName: string;
  confidence: ForeignAddressConfidence;
  /** Why the reading is not high, in plain words. Empty for a clean high reading. */
  reasons: string[];
}

export interface ForeignAddressOptions {
  /** The record's mailing address line, checked for a country when the block names none. */
  addressLine?: string;
}

// ---------------------------------------------------------------------------
// Text normalisation
// ---------------------------------------------------------------------------

/** Upper case, no accents, no periods or apostrophes, & as AND, single spaces. */
export function normalizeAddressText(value: string): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[.']/g, "")
    .replace(/&/g, " AND ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The key a country or province name is looked up by: normalised, commas and dashes as spaces. */
function nameKey(value: string): string {
  return normalizeAddressText(value).replace(/[,\-/]/g, " ").replace(/\s+/g, " ").trim();
}

// ---------------------------------------------------------------------------
// Countries
// ---------------------------------------------------------------------------

/** Codes Pub 1220 and USPS use as US states, territories and military post offices. */
export const US_STATE_CODES = new Set(
  (
    "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH " +
    "NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY AS GU MP PR VI FM MH PW AA AE AP"
  ).split(" ")
);

/** IRS codes of the US territories. Pub 1220 files these as US addresses, not foreign ones. */
const US_TERRITORY_COUNTRY_CODES = new Set(["RQ", "GQ", "VQ", "AQ", "CQ"]);

/**
 * Names the IRS table does not carry but filers write. Each maps to an IRS name that the
 * generated table resolves, so the code always comes from the schema.
 */
const COUNTRY_ALIASES: Record<string, string> = {
  "UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND": "UNITED KINGDOM",
  BRITAIN: "UNITED KINGDOM",
  DEUTSCHLAND: "GERMANY",
  "FEDERAL REPUBLIC OF GERMANY": "GERMANY",
  HOLLAND: "NETHERLANDS",
  "THE NETHERLANDS": "NETHERLANDS",
  "PEOPLES REPUBLIC OF CHINA": "CHINA",
  "P R CHINA": "CHINA",
  "PR CHINA": "CHINA",
  PRC: "CHINA",
  "REPUBLIC OF KOREA": "SOUTH KOREA",
  "KOREA SOUTH": "SOUTH KOREA",
  "S KOREA": "SOUTH KOREA",
  "VIET NAM": "VIETNAM",
  UAE: "UNITED ARAB EMIRATES",
  "U A E": "UNITED ARAB EMIRATES",
  "HONG KONG SAR": "HONG KONG",
  HONGKONG: "HONG KONG",
  "REPUBLIC OF IRELAND": "IRELAND",
  EIRE: "IRELAND",
  ESPANA: "SPAIN",
  ITALIA: "ITALY",
  BRASIL: "BRAZIL",
  SUISSE: "SWITZERLAND",
  SCHWEIZ: "SWITZERLAND",
  CZECHIA: "CZECH REPUBLIC",
  "RUSSIAN FEDERATION": "RUSSIA",
  TURKIYE: "TURKEY",
  "REPUBLIC OF THE PHILIPPINES": "PHILIPPINES",
  "REPUBLIC OF SOUTH AFRICA": "SOUTH AFRICA",
  RSA: "SOUTH AFRICA",
};

/** A country named as the United States: never a foreign address. */
const UNITED_STATES_NAMES = new Set([
  "USA",
  "US",
  "U S A",
  "U S",
  "UNITED STATES",
  "UNITED STATES OF AMERICA",
  "AMERICA",
]);

/** The name to print for a code where the schema's own name is awkward. */
const DISPLAY_NAME: Record<string, string> = {
  UK: "UNITED KINGDOM",
  KS: "SOUTH KOREA",
  KN: "NORTH KOREA",
  YM: "YEMEN",
  IV: "COTE D'IVOIRE",
};

/**
 * ISO 3166-1 alpha-2 codes for the countries a US filer most often pays, by IRS name. Filers
 * write ISO codes as often as IRS ones, and the two lists disagree (ISO GB is the United
 * Kingdom, IRS GB is Gabon; ISO CH is Switzerland, IRS CH is China). A two-letter country is
 * therefore only taken when both readings agree or the address itself settles it.
 */
const ISO2_TO_IRS_NAME: Record<string, string> = {
  AD: "ANDORRA", AE: "UNITED ARAB EMIRATES", AR: "ARGENTINA", AT: "AUSTRIA", AU: "AUSTRALIA",
  BE: "BELGIUM", BM: "BERMUDA", BR: "BRAZIL", BS: "BAHAMAS", CA: "CANADA", CH: "SWITZERLAND",
  CL: "CHILE", CN: "CHINA", CO: "COLOMBIA", CR: "COSTA RICA", CZ: "CZECH REPUBLIC",
  DE: "GERMANY", DK: "DENMARK", DO: "DOMINICAN REPUBLIC", EG: "EGYPT", ES: "SPAIN",
  FI: "FINLAND", FR: "FRANCE", GB: "UNITED KINGDOM", GE: "GEORGIA", GR: "GREECE",
  GT: "GUATEMALA", HK: "HONG KONG", HN: "HONDURAS", HU: "HUNGARY", ID: "INDONESIA",
  IE: "IRELAND", IL: "ISRAEL", IN: "INDIA", IS: "ICELAND", IT: "ITALY", JM: "JAMAICA",
  JO: "JORDAN", JP: "JAPAN", KE: "KENYA", KR: "SOUTH KOREA", KW: "KUWAIT", KY: "CAYMAN ISLANDS",
  LU: "LUXEMBOURG", MX: "MEXICO", MY: "MALAYSIA", NG: "NIGERIA", NL: "NETHERLANDS", NO: "NORWAY",
  NZ: "NEW ZEALAND", PA: "PANAMA", PE: "PERU", PH: "PHILIPPINES", PK: "PAKISTAN", PL: "POLAND",
  PR: "PUERTO RICO", PT: "PORTUGAL", QA: "QATAR", RO: "ROMANIA", RU: "RUSSIA",
  SA: "SAUDI ARABIA", SE: "SWEDEN", SG: "SINGAPORE", SV: "EL SALVADOR", TH: "THAILAND",
  TR: "TURKEY", TW: "TAIWAN", UA: "UKRAINE", VG: "BRITISH VIRGIN ISLANDS", VN: "VIETNAM",
  ZA: "SOUTH AFRICA",
};

/** ISO 3166-1 alpha-3 codes. Three letters never collide with an IRS code. */
const ISO3_TO_IRS_NAME: Record<string, string> = {
  CAN: "CANADA", MEX: "MEXICO", GBR: "UNITED KINGDOM", DEU: "GERMANY", FRA: "FRANCE",
  ITA: "ITALY", ESP: "SPAIN", NLD: "NETHERLANDS", BEL: "BELGIUM", CHE: "SWITZERLAND",
  AUT: "AUSTRIA", IRL: "IRELAND", AUS: "AUSTRALIA", NZL: "NEW ZEALAND", JPN: "JAPAN",
  CHN: "CHINA", IND: "INDIA", KOR: "SOUTH KOREA", SGP: "SINGAPORE", HKG: "HONG KONG",
  BRA: "BRAZIL", ARG: "ARGENTINA", CHL: "CHILE", COL: "COLOMBIA", PER: "PERU",
  ISR: "ISRAEL", ZAF: "SOUTH AFRICA", ARE: "UNITED ARAB EMIRATES", PHL: "PHILIPPINES",
  SWE: "SWEDEN", NOR: "NORWAY", DNK: "DENMARK", FIN: "FINLAND", PRT: "PORTUGAL",
  POL: "POLAND", GRC: "GREECE", TUR: "TURKEY",
};

function irsCodeForName(name: string): string | null {
  return IRS_COUNTRY_CODES[name] ?? null;
}

/** Normalised country name -> IRS code: the schema's names, their variants and the aliases. */
const COUNTRY_BY_NAME: Map<string, string> = (() => {
  const map = new Map<string, string>();
  const add = (name: string, code: string) => {
    const key = nameKey(name);
    if (key && !map.has(key)) map.set(key, code);
    // "SAINT KITTS" is written "ST KITTS"; "TRINIDAD AND TOBAGO" as "TRINIDAD & TOBAGO".
    const variants = [key.replace(/\bSAINT\b/g, "ST"), key.replace(/^THE /, "")];
    for (const v of variants) if (v && !map.has(v)) map.set(v, code);
  };
  for (const [name, code] of Object.entries(IRS_COUNTRY_CODES)) {
    if (name.length > 2) add(name, code);
  }
  for (const [alias, target] of Object.entries(COUNTRY_ALIASES)) {
    const code = irsCodeForName(target);
    if (code) add(alias, code);
  }
  return map;
})();

/** The IRS name to print for a code. */
export function countryDisplayName(code: string): string {
  if (DISPLAY_NAME[code]) return DISPLAY_NAME[code];
  for (const [name, c] of Object.entries(IRS_COUNTRY_CODES)) {
    if (c === code && name.length > 2) return name;
  }
  return code;
}

// ---------------------------------------------------------------------------
// Per-country postal codes and provinces
// ---------------------------------------------------------------------------

interface CountryProfile {
  /** Whole-string patterns for the postal code, as written (spaces allowed where shown). */
  postal: RegExp[];
  /** True when the format comes from the UPU addressing sheet for the country. */
  verified: boolean;
  /** Where the postal code sits relative to the town, by the country's own practice. */
  postalPosition: "before" | "after";
  /** The country has no postal codes. */
  noPostal?: boolean;
  /** Province or state code -> full name, where the country uses codes on addresses. */
  provinces?: Record<string, string>;
}

const CANADA_PROVINCES: Record<string, string> = {
  AB: "ALBERTA", BC: "BRITISH COLUMBIA", MB: "MANITOBA", NB: "NEW BRUNSWICK",
  NL: "NEWFOUNDLAND AND LABRADOR", NT: "NORTHWEST TERRITORIES", NS: "NOVA SCOTIA",
  NU: "NUNAVUT", ON: "ONTARIO", PE: "PRINCE EDWARD ISLAND", QC: "QUEBEC", SK: "SASKATCHEWAN",
  YT: "YUKON",
};
/** Older Canada Post symbols still found in files: Newfoundland, Quebec, Yukon. */
const CANADA_OLD_CODES: Record<string, string> = { NF: "NL", PQ: "QC", YK: "YT" };

const AUSTRALIA_STATES: Record<string, string> = {
  ACT: "AUSTRALIAN CAPITAL TERRITORY", NSW: "NEW SOUTH WALES", NT: "NORTHERN TERRITORY",
  QLD: "QUEENSLAND", SA: "SOUTH AUSTRALIA", TAS: "TASMANIA", VIC: "VICTORIA",
  WA: "WESTERN AUSTRALIA",
};

/** Correos de Mexico abbreviations (UPU sheet). They are not two letters, and not standard. */
const MEXICO_STATES: Record<string, string> = {
  AGS: "AGUASCALIENTES", BC: "BAJA CALIFORNIA", BCS: "BAJA CALIFORNIA SUR", CAM: "CAMPECHE",
  COAH: "COAHUILA", COL: "COLIMA", CHIS: "CHIAPAS", CHIH: "CHIHUAHUA",
  CDMX: "CIUDAD DE MEXICO", DGO: "DURANGO", GTO: "GUANAJUATO", GRO: "GUERRERO",
  HGO: "HIDALGO", JAL: "JALISCO", MEX: "ESTADO DE MEXICO", MICH: "MICHOACAN",
  MOR: "MORELOS", NAY: "NAYARIT", NL: "NUEVO LEON", OAX: "OAXACA", PUE: "PUEBLA",
  QRO: "QUERETARO", "Q ROO": "QUINTANA ROO", SLP: "SAN LUIS POTOSI", SIN: "SINALOA",
  SON: "SONORA", TAB: "TABASCO", TAMPS: "TAMAULIPAS", TLAX: "TLAXCALA", VER: "VERACRUZ",
  YUC: "YUCATAN", ZAC: "ZACATECAS",
};
const MEXICO_OLD_CODES: Record<string, string> = {
  DF: "CDMX", "D F": "CDMX", "DISTRITO FEDERAL": "CDMX", QROO: "Q ROO", "EDO MEX": "MEX",
  EDOMEX: "MEX",
};

const BRAZIL_STATES: Record<string, string> = {
  AC: "ACRE", AL: "ALAGOAS", AP: "AMAPA", AM: "AMAZONAS", BA: "BAHIA", CE: "CEARA",
  DF: "DISTRITO FEDERAL", ES: "ESPIRITO SANTO", GO: "GOIAS", MA: "MARANHAO", MT: "MATO GROSSO",
  MS: "MATO GROSSO DO SUL", MG: "MINAS GERAIS", PA: "PARA", PB: "PARAIBA", PR: "PARANA",
  PE: "PERNAMBUCO", PI: "PIAUI", RJ: "RIO DE JANEIRO", RN: "RIO GRANDE DO NORTE",
  RS: "RIO GRANDE DO SUL", RO: "RONDONIA", RR: "RORAIMA", SC: "SANTA CATARINA",
  SP: "SAO PAULO", SE: "SERGIPE", TO: "TOCANTINS",
};

const p = (...patterns: string[]) => patterns.map((s) => new RegExp(`^(?:${s})$`));

/** Profiles by IRS country name; resolved to IRS codes below so the code is the schema's. */
const PROFILES_BY_NAME: Record<string, CountryProfile> = {
  // Verified against the UPU addressing sheets.
  CANADA: {
    postal: p("[ABCEGHJ-NPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z] ?\\d[ABCEGHJ-NPRSTV-Z]\\d"),
    verified: true, postalPosition: "after", provinces: CANADA_PROVINCES,
  },
  "UNITED KINGDOM": {
    postal: p("GIR ?0AA", "[A-Z]{1,2}\\d[A-Z\\d]? ?\\d[A-Z]{2}"),
    verified: true, postalPosition: "after",
  },
  MEXICO: { postal: p("\\d{5}"), verified: true, postalPosition: "before", provinces: MEXICO_STATES },
  BRAZIL: { postal: p("\\d{5}-?\\d{3}"), verified: true, postalPosition: "after", provinces: BRAZIL_STATES },
  NETHERLANDS: { postal: p("\\d{4} ?[A-Z]{2}"), verified: true, postalPosition: "before" },
  GERMANY: { postal: p("\\d{5}"), verified: true, postalPosition: "before" },
  FRANCE: { postal: p("\\d{5}"), verified: true, postalPosition: "before" },
  AUSTRALIA: { postal: p("\\d{4}"), verified: true, postalPosition: "after", provinces: AUSTRALIA_STATES },
  CHINA: { postal: p("\\d{6}"), verified: true, postalPosition: "before" },
  INDIA: { postal: p("\\d{6}", "\\d{3} \\d{3}"), verified: true, postalPosition: "after" },
  IRELAND: {
    postal: p("[AC-FHKNPRTV-Y]\\d{2} ?[0-9AC-FHKNPRTV-Y]{4}", "D6W ?[0-9AC-FHKNPRTV-Y]{4}"),
    verified: true, postalPosition: "after",
  },
  "HONG KONG": { postal: [], verified: true, postalPosition: "after", noPostal: true },
  "UNITED ARAB EMIRATES": { postal: [], verified: true, postalPosition: "after", noPostal: true },
  // Widely published national formats, not checked against a UPU sheet here.
  JAPAN: { postal: p("\\d{3}-?\\d{4}"), verified: false, postalPosition: "after" },
  ITALY: { postal: p("\\d{5}"), verified: false, postalPosition: "before" },
  SPAIN: { postal: p("\\d{5}"), verified: false, postalPosition: "before" },
  SWITZERLAND: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  AUSTRIA: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  BELGIUM: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  DENMARK: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  NORWAY: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  SWEDEN: { postal: p("\\d{3} ?\\d{2}"), verified: false, postalPosition: "before" },
  FINLAND: { postal: p("\\d{5}"), verified: false, postalPosition: "before" },
  PORTUGAL: { postal: p("\\d{4}-\\d{3}"), verified: false, postalPosition: "before" },
  LUXEMBOURG: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  POLAND: { postal: p("\\d{2}-\\d{3}"), verified: false, postalPosition: "before" },
  "CZECH REPUBLIC": { postal: p("\\d{3} ?\\d{2}"), verified: false, postalPosition: "before" },
  GREECE: { postal: p("\\d{3} ?\\d{2}"), verified: false, postalPosition: "before" },
  TURKEY: { postal: p("\\d{5}"), verified: false, postalPosition: "before" },
  ROMANIA: { postal: p("\\d{6}"), verified: false, postalPosition: "before" },
  HUNGARY: { postal: p("\\d{4}"), verified: false, postalPosition: "before" },
  ICELAND: { postal: p("\\d{3}"), verified: false, postalPosition: "before" },
  RUSSIA: { postal: p("\\d{6}"), verified: false, postalPosition: "before" },
  ISRAEL: { postal: p("\\d{7}", "\\d{5}"), verified: false, postalPosition: "after" },
  "SOUTH KOREA": { postal: p("\\d{5}"), verified: false, postalPosition: "after" },
  SINGAPORE: { postal: p("\\d{6}"), verified: false, postalPosition: "after" },
  TAIWAN: { postal: p("\\d{3}(?:\\d{2,3})?"), verified: false, postalPosition: "after" },
  PHILIPPINES: { postal: p("\\d{4}"), verified: false, postalPosition: "after" },
  "NEW ZEALAND": { postal: p("\\d{4}"), verified: false, postalPosition: "after" },
  "SOUTH AFRICA": { postal: p("\\d{4}"), verified: false, postalPosition: "after" },
  ARGENTINA: { postal: p("[A-Z]\\d{4}[A-Z]{3}", "\\d{4}"), verified: false, postalPosition: "before" },
  CHILE: { postal: p("\\d{7}"), verified: false, postalPosition: "after" },
  COLOMBIA: { postal: p("\\d{6}"), verified: false, postalPosition: "after" },
  VIETNAM: { postal: p("\\d{6}"), verified: false, postalPosition: "after" },
  THAILAND: { postal: p("\\d{5}"), verified: false, postalPosition: "after" },
  MALAYSIA: { postal: p("\\d{5}"), verified: false, postalPosition: "before" },
  INDONESIA: { postal: p("\\d{5}"), verified: false, postalPosition: "after" },
  BERMUDA: { postal: p("[A-Z]{2} ?(?:\\d{2}|[A-Z]{2})"), verified: false, postalPosition: "after" },
  "CAYMAN ISLANDS": { postal: p("KY\\d-\\d{4}"), verified: false, postalPosition: "after" },
  BAHAMAS: { postal: [], verified: false, postalPosition: "after", noPostal: true },
  QATAR: { postal: [], verified: false, postalPosition: "after", noPostal: true },
};

const PROFILES: Map<string, CountryProfile> = (() => {
  const map = new Map<string, CountryProfile>();
  for (const [name, profile] of Object.entries(PROFILES_BY_NAME)) {
    const code = irsCodeForName(name);
    if (code) map.set(code, profile);
  }
  return map;
})();

/** For tests: every profile name must resolve through the schema's table. */
export const FOREIGN_PROFILE_NAMES = Object.keys(PROFILES_BY_NAME);

/** Postal code labels written in front of the code itself. */
const POSTAL_LABELS = new Set(["CP", "CEP", "PIN", "PINCODE", "POSTCODE", "ZIP", "PLZ", "NPA"]);

/** Country prefixes written onto European postal codes: "CH-8001", "D-10115", "F-75008". */
const POSTAL_PREFIX = /^(?:[A-Z]{1,3})-(.+)$/;

/** Words that join a place name to the word after it ("CIUDAD DE MEXICO", "PORT OF SPAIN"). */
const CONNECTORS = new Set(["DE", "DEL", "DA", "DO", "DOS", "DAS", "OF", "LA", "LE", "EL", "DI"]);

// ---------------------------------------------------------------------------
// The reader
// ---------------------------------------------------------------------------

interface Token {
  text: string;
  /** A comma came directly after this token. */
  comma: boolean;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  const cleaned = normalizeAddressText(text).replace(/\s*,\s*/g, " , ");
  for (const part of cleaned.split(" ")) {
    if (!part) continue;
    if (part === ",") {
      if (tokens.length) tokens[tokens.length - 1].comma = true;
      continue;
    }
    // A dash on its own separates ("BRASILIA - DF"); it is not part of a name.
    if (/^[-–]+$/.test(part)) {
      if (tokens.length) tokens[tokens.length - 1].comma = true;
      continue;
    }
    tokens.push({ text: part, comma: false });
  }
  return tokens;
}

const joinTokens = (tokens: Token[]) => tokens.map((t) => t.text).join(" ");

interface CountryMatch {
  code: string | null;
  start: number;
  end: number;
  /** How the country was written: a name, a 2- or 3-letter code. */
  kind: "name" | "iso3" | "code2" | "us";
  /** For a two-letter code: every country it could mean. */
  candidates?: string[];
}

/** The longest country name ending at token `end` (exclusive), if any. */
function countryEndingAt(tokens: Token[], end: number): CountryMatch | null {
  for (let n = Math.min(8, end); n >= 1; n--) {
    const start = end - n;
    const key = nameKey(joinTokens(tokens.slice(start, end)));
    if (UNITED_STATES_NAMES.has(key)) return { code: null, start, end, kind: "us" };
    const code = COUNTRY_BY_NAME.get(key);
    if (code) return { code, start, end, kind: "name" };
  }
  const last = tokens[end - 1]?.text ?? "";
  if (/^[A-Z]{3}$/.test(last) && ISO3_TO_IRS_NAME[last]) {
    return { code: irsCodeForName(ISO3_TO_IRS_NAME[last]), start: end - 1, end, kind: "iso3" };
  }
  if (/^[A-Z]{2}$/.test(last)) {
    const candidates = new Set<string>();
    if (IRS_COUNTRY_CODES[last] === last) candidates.add(last);
    const iso = ISO2_TO_IRS_NAME[last];
    const isoCode = iso ? irsCodeForName(iso) : null;
    if (isoCode) candidates.add(isoCode);
    if (candidates.size) return { code: null, start: end - 1, end, kind: "code2", candidates: [...candidates] };
  }
  return null;
}

/**
 * A country name cut off by the end of the 51-character field: "KOREA, REPUB" for "KOREA,
 * REPUBLIC OF". Only the last few tokens are tried, letters only, at least four of them, and
 * the cut name must begin exactly one country's name ("KOREA" alone begins both Koreas).
 */
function truncatedCountryAt(tokens: Token[]): CountryMatch | null {
  for (let n = Math.min(4, tokens.length - 1); n >= 1; n--) {
    const start = tokens.length - n;
    const run = tokens.slice(start);
    if (run.some((t) => /\d/.test(t.text))) continue;
    const key = nameKey(joinTokens(run));
    if (key.replace(/ /g, "").length < 4) continue;
    const codes = new Set<string>();
    for (const [name, code] of COUNTRY_BY_NAME) {
      if (name.startsWith(key)) codes.add(code);
    }
    if (codes.size === 1) return { code: [...codes][0], start, end: tokens.length, kind: "name" };
    if (codes.size > 1) return null;
  }
  return null;
}

interface PartsReading {
  city: string;
  province: string;
  provinceName: string;
  postalCode: string;
  /** The postal code matched the country's own format. */
  postalMatched: boolean;
  /** A province code or name of the country was found. */
  provinceMatched: boolean;
  /** Something about the reading that a person should check. */
  doubts: string[];
}

function matchesPostal(profile: CountryProfile | undefined, value: string): boolean {
  if (!profile) return false;
  return profile.postal.some((re) => re.test(value));
}

/** Strip a postal label ("CP 06600") or a country prefix ("CH-8001") the code carries. */
function cleanPostalTokens(tokens: Token[], profile: CountryProfile | undefined): Token[] {
  const out: Token[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (POSTAL_LABELS.has(t.text) && i + 1 < tokens.length && /\d/.test(tokens[i + 1].text)) continue;
    const prefixed = POSTAL_PREFIX.exec(t.text);
    if (prefixed && profile && matchesPostal(profile, prefixed[1])) {
      out.push({ ...t, text: prefixed[1] });
      continue;
    }
    out.push(t);
  }
  return out;
}

/** Find the province: a code or full name of the country, next to the postal code or at the end. */
function takeProvince(
  tokens: Token[],
  provinces: Record<string, string>,
  oldCodes: Record<string, string>
): { index: number; length: number; code: string } | null {
  const byName = new Map<string, string>();
  for (const [code, name] of Object.entries(provinces)) {
    byName.set(nameKey(code), code);
    byName.set(nameKey(name), code);
  }
  for (const [old, code] of Object.entries(oldCodes)) byName.set(nameKey(old), code);
  // The province follows the town in the stated order, so look from the end backwards, and
  // never take the very first token: that is the town ("QUEBEC QC" is the town of Quebec).
  for (let end = tokens.length; end >= 2; end--) {
    for (let n = Math.min(4, end - 1); n >= 1; n--) {
      const start = end - n;
      const key = nameKey(joinTokens(tokens.slice(start, end)));
      const code = byName.get(key);
      if (code) return { index: start, length: n, code };
    }
    // Only the last token or two can be the province; further in is the town.
    if (tokens.length - end >= 1) break;
  }
  return null;
}

/** Read what is left once the country is out: town, province and postal code. */
function readParts(input: Token[], countryCode: string | null): PartsReading {
  const profile = countryCode ? PROFILES.get(countryCode) : undefined;
  let tokens = cleanPostalTokens(input, profile);
  const doubts: string[] = [];

  // 1. The postal code: a token, or two tokens with the space the format allows.
  let postalCode = "";
  let postalMatched = false;
  const pick = (index: number, length: number, value: string, matched: boolean) => {
    postalCode = value;
    postalMatched = matched;
    tokens = [...tokens.slice(0, index), ...tokens.slice(index + length)];
  };
  if (profile && !profile.noPostal) {
    const found: { index: number; length: number; value: string }[] = [];
    for (let i = 0; i < tokens.length; i++) {
      if (i + 1 < tokens.length) {
        const pair = `${tokens[i].text} ${tokens[i + 1].text}`;
        if (matchesPostal(profile, pair)) found.push({ index: i, length: 2, value: pair });
      }
      if (matchesPostal(profile, tokens[i].text)) found.push({ index: i, length: 1, value: tokens[i].text });
    }
    if (found.length) {
      // Prefer the side the country writes it on; a pair over a single token at the same place.
      const ordered = [...found].sort((a, b) =>
        profile.postalPosition === "after" ? b.index - a.index || b.length - a.length : a.index - b.index || b.length - a.length
      );
      const best = ordered[0];
      pick(best.index, best.length, best.value, true);
    }
  }
  // A Dublin postal district ("DUBLIN 2", "DUBLIN 6W") is part of the town, not a postal code.
  const dublinDistrict =
    countryCode === "EI" &&
    tokens.length >= 2 &&
    tokens[tokens.length - 2].text === "DUBLIN" &&
    /^\d{1,2}W?$/.test(tokens[tokens.length - 1].text);
  if (!postalCode && !dublinDistrict) {
    // No profile match: the run of digit-bearing tokens at the end (the stated order), else at
    // the start (the European order). Never from the middle of the town's name.
    const hasDigit = (t: Token) => /\d/.test(t.text);
    let end = tokens.length;
    let start = end;
    while (start > 0 && hasDigit(tokens[start - 1]) && end - start < 2) start--;
    if (start < end && start > 0) {
      pick(start, end - start, joinTokens(tokens.slice(start, end)), false);
    } else {
      end = 0;
      while (end < tokens.length && hasDigit(tokens[end]) && end < 2) end++;
      if (end > 0 && end < tokens.length) pick(0, end, joinTokens(tokens.slice(0, end)), false);
    }
    if (postalCode && profile && !profile.noPostal) {
      doubts.push(`"${postalCode}" is not in the usual postal code format for ${countryDisplayName(countryCode!)}.`);
    } else if (postalCode && profile?.noPostal) {
      doubts.push(`${countryDisplayName(countryCode!)} has no postal codes, but the address carries "${postalCode}".`);
    } else if (postalCode && !profile) {
      doubts.push(`The postal code "${postalCode}" could not be checked: there is no postal code format on file for this country.`);
    }
  }

  // 2. The province.
  let province = "";
  let provinceName = "";
  let provinceMatched = false;
  const lists: Record<string, [Record<string, string>, Record<string, string>]> = {
    CA: [CANADA_PROVINCES, CANADA_OLD_CODES],
    AS: [AUSTRALIA_STATES, {}],
    MX: [MEXICO_STATES, MEXICO_OLD_CODES],
    BR: [BRAZIL_STATES, {}],
  };
  const list = countryCode ? lists[countryCode] : undefined;
  if (list) {
    const hit = takeProvince(tokens, list[0], list[1]);
    if (hit) {
      province = hit.code;
      provinceName = list[0][hit.code] ?? hit.code;
      provinceMatched = true;
      tokens = [...tokens.slice(0, hit.index), ...tokens.slice(hit.index + hit.length)];
    }
  }
  if (!province) {
    // Comma-separated, as the order asks: town, province. Take the province only when the
    // filer separated it with a comma.
    const commaAt = tokens.findIndex((t, i) => t.comma && i < tokens.length - 1);
    if (commaAt >= 0) {
      const rest = tokens.slice(commaAt + 1);
      if (rest.length <= 3 && !rest.some((t) => /\d/.test(t.text))) {
        province = joinTokens(rest);
        provinceName = province;
        tokens = tokens.slice(0, commaAt + 1);
        if (list) doubts.push(`"${province}" is not a ${countryDisplayName(countryCode!)} province or state the reader knows.`);
      }
    }
  }

  // 3. The town is what remains.
  const city = joinTokens(tokens);
  if (!city) doubts.push("No town or city could be read.");
  if (/\d/.test(city) && !/\bCEDEX\b/.test(city) && !dublinDistrict) {
    doubts.push(`The town "${city}" still contains digits.`);
  }

  return { city, province, provinceName, postalCode, postalMatched, provinceMatched, doubts };
}

/** How well a country's own format supports reading the parts as that country. */
function evidenceFor(code: string, rest: Token[]): number {
  const parts = readParts(rest, code);
  return (parts.postalMatched ? 2 : 0) + (parts.provinceMatched ? 2 : 0) - parts.doubts.length;
}

/** True when the fixed-position state and ZIP read as a US address. */
function looksLikeUsAddress(block: string): boolean {
  const state = block.slice(40, 42).trim().toUpperCase();
  const zip = block.slice(42, 51).trim();
  return US_STATE_CODES.has(state) && /^\d{5}(?:\d{4})?$/.test(zip);
}

function reading(
  raw: string,
  parts: PartsReading,
  code: string | null,
  confidence: ForeignAddressConfidence,
  reasons: string[]
): ForeignAddressReading {
  return {
    raw,
    city: parts.city,
    province: parts.province,
    provinceName: parts.provinceName,
    postalCode: parts.postalCode,
    countryCode: code,
    countryName: code ? countryDisplayName(code) : "",
    confidence,
    reasons,
  };
}

/** Read one candidate text (the block as written, or split at the fixed positions). */
function readText(raw: string, text: string, options: ForeignAddressOptions, block: string): ForeignAddressReading {
  const tokens = tokenize(text);
  const reasons: string[] = [];
  const empty: PartsReading = {
    city: normalizeAddressText(text), province: "", provinceName: "", postalCode: "",
    postalMatched: false, provinceMatched: false, doubts: [],
  };
  if (!tokens.length) return reading(raw, { ...empty, city: "" }, null, "low", ["The city, state and ZIP positions are blank."]);

  // 1. The country, at the end where Pub 1220 puts it.
  let match = countryEndingAt(tokens, tokens.length);
  let source: "block" | "block-start" | "block-cut" | "address" | "inferred" = "block";

  // A country written first and set off by a comma ("CANADA, TORONTO ON ...").
  if (!match) {
    for (let n = Math.min(6, tokens.length - 1); n >= 1; n--) {
      if (!tokens[n - 1].comma) continue;
      const key = nameKey(joinTokens(tokens.slice(0, n)));
      const code = COUNTRY_BY_NAME.get(key);
      if (code) {
        match = { code, start: 0, end: n, kind: "name" };
        source = "block-start";
        break;
      }
    }
  }

  // The block runs to its last position and ends in the start of a country's name.
  if (!match && block.trimEnd().length === 51) {
    match = truncatedCountryAt(tokens);
    if (match) source = "block-cut";
  }

  if (match?.kind === "us") {
    const rest = [...tokens.slice(0, match.start), ...tokens.slice(match.end)];
    return reading(raw, readParts(rest, null), null, "low", [
      "The address names the United States but is marked foreign. Check the foreign indicator.",
    ]);
  }

  let rest = match ? [...tokens.slice(0, match.start), ...tokens.slice(match.end)] : tokens;
  let code = match?.code ?? null;

  // A place name that ends in a country's name ("CIUDAD DE MEXICO"): the country stays in it.
  if (match && match.kind === "name" && rest.length && CONNECTORS.has(rest[rest.length - 1].text)) {
    rest = tokens;
    reasons.push(`"${joinTokens(tokens.slice(match.start, match.end))}" may be part of the place name, not only the country.`);
  }

  // 2. A two-letter code: take it only when it means one country, or the address settles it.
  if (match?.kind === "code2") {
    const candidates = match.candidates ?? [];
    const token = tokens[match.start].text;
    const scored = candidates
      .map((c) => ({ c, score: evidenceFor(c, rest) }))
      .sort((a, b) => b.score - a.score);
    const best = scored[0];
    const second = scored[1];
    const settled = best && best.score >= 2 && (!second || second.score < best.score);
    if (candidates.length === 1 && !US_STATE_CODES.has(token)) {
      code = candidates[0];
      if (!settled) reasons.push(`The country is written as the code "${token}"; read as ${countryDisplayName(code)}.`);
    } else if (settled) {
      code = best.c;
      reasons.push(`The code "${token}" can mean more than one place; the postal code and province say ${countryDisplayName(code)}.`);
    } else {
      const meanings = candidates.map((c) => countryDisplayName(c));
      if (US_STATE_CODES.has(token)) meanings.push("a US state or territory");
      return reading(raw, readParts(tokens, null), null, "low", [
        `"${token}" could mean ${meanings.join(" or ")}. Write the country's name.`,
      ]);
    }
  }
  if (match?.kind === "iso3" && code) {
    reasons.push(`The country is written as the code "${tokens[match.start].text}"; read as ${countryDisplayName(code)}.`);
  }
  if (source === "block-start") reasons.push("The country is written first rather than last.");
  if (source === "block-cut" && match && code) {
    reasons.push(
      `The country name is cut off at the end of the field ("${joinTokens(tokens.slice(match.start, match.end))}"); only ${countryDisplayName(code)} begins that way.`
    );
  }

  // 3. No country in the block: the address line, then a postal code distinctive enough.
  if (!code) {
    const line = tokenize(options.addressLine ?? "");
    const fromLine = line.length ? countryEndingAt(line, line.length) : null;
    if (fromLine?.kind === "name" && fromLine.code) {
      code = fromLine.code;
      source = "address";
      reasons.push(`The country (${countryDisplayName(code)}) is written in the address line, not the city, state and ZIP positions.`);
    }
  }
  if (!code) {
    if (looksLikeUsAddress(block)) {
      return reading(raw, readParts(tokens, null), null, "low", [
        "This reads as a US address (a US state code and ZIP code) but is marked foreign. Check the foreign indicator.",
      ]);
    }
    // Only formats no other country shares are used to name a country the filer did not write.
    for (const candidate of ["CA", "UK", "NL", "BR"]) {
      const parts = readParts(tokens, candidate);
      const strong =
        parts.postalMatched &&
        parts.doubts.length === 0 &&
        (candidate === "UK" || candidate === "NL" || parts.provinceMatched);
      if (strong) {
        code = candidate;
        source = "inferred";
        reasons.push(`No country is written; ${countryDisplayName(candidate)} is inferred from the postal code${parts.provinceMatched ? " and province" : ""}.`);
        break;
      }
    }
  }
  if (!code) {
    return reading(raw, readParts(tokens, null), null, "low", [
      "No country could be read. Pub 1220 asks for the country's name after the postal code.",
    ]);
  }
  if (US_TERRITORY_COUNTRY_CODES.has(code)) {
    return reading(raw, readParts(rest, code), code, "low", [
      `${countryDisplayName(code)} is a US territory. Pub 1220 files it as a US address with its state code, not as foreign.`,
    ]);
  }

  // 4. Town, province, postal code.
  const parts = readParts(rest, code);
  const profile = PROFILES.get(code);
  reasons.push(...parts.doubts);
  const dublin = code === "EI" && /^DUBLIN \d{1,2}W?$/.test(parts.city);
  if (profile && !profile.noPostal && !parts.postalCode && !dublin) {
    reasons.push(`No postal code was found for ${countryDisplayName(code)}.`);
  }
  if (profile?.noPostal && !profile.verified) {
    reasons.push(`${countryDisplayName(code)} is taken to have no postal codes; that is not checked against a UPU sheet.`);
  }
  if (profile && !profile.verified && parts.postalMatched) {
    reasons.push(`The postal code format for ${countryDisplayName(code)} is the published national one, not checked against a UPU sheet.`);
  }
  if (!profile && !parts.postalCode) {
    reasons.push(`There is no postal code format on file for ${countryDisplayName(code)}; check that none is missing.`);
  }

  const contradicts = parts.doubts.some((d) => d.includes("not in the usual postal code format") || d.includes("has no postal codes"));
  const lost = !parts.city;
  let confidence: ForeignAddressConfidence;
  if (lost || contradicts) confidence = "low";
  else if (reasons.length === 0) confidence = "high";
  else confidence = "medium";
  return reading(raw, parts, code, confidence, reasons);
}

const RANK: Record<ForeignAddressConfidence, number> = { high: 3, medium: 2, low: 1 };

/**
 * Read a Pub 1220 foreign city/state/ZIP block (51 characters: city 40, state 2, ZIP 9).
 *
 * The block is read twice: as one free-format run of text, which is what Pub 1220 describes,
 * and split at the fixed City/State/ZIP positions, for writers who kept the US layout and put
 * a province and postal code in the state and ZIP positions. The better reading wins; on a tie
 * the free-format one does.
 */
export function readForeignCityBlock(block: string, options: ForeignAddressOptions = {}): ForeignAddressReading {
  const padded = String(block ?? "").padEnd(51, " ").slice(0, 51);
  const raw = padded.trim();
  const asRun = readText(raw, padded, options, padded);
  const split = `${padded.slice(0, 40).trimEnd()} ${padded.slice(40, 42).trim()} ${padded.slice(42, 51).trim()}`;
  if (split.replace(/\s+/g, " ").trim() === padded.replace(/\s+/g, " ").trim()) return asRun;
  const asFields = readText(raw, split, options, padded);
  return RANK[asFields.confidence] > RANK[asRun.confidence] ? asFields : asRun;
}

/**
 * A province value safe for the Tab portal's upload check, which refuses a US state code on a
 * foreign row: Western Australia's "WA" or Para's "PA" is written out in full instead.
 */
export function provinceForUpload(read: Pick<ForeignAddressReading, "province" | "provinceName">): string {
  if (read.province && US_STATE_CODES.has(read.province) && read.provinceName && read.provinceName !== read.province) {
    return read.provinceName;
  }
  return read.province;
}
